import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import notification from "antd/es/notification";
import cn from "classnames";
import { useIntl } from "react-intl";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";

import DnDBoard from "@app/component/DnDBoard";
import BoardColumn, { BoardColumnId } from "@app/component/DnDBoard/types/BoardColumn";
import DownloadEntitiesModal from "@app/component/DownloadEntitiesModal";
import ViewComponent from "@app/component/view/types/ViewComponent";
import useView from "@app/component/view/utils/useView";
import { getKanbanHiddenColumnIds } from "@app/scene/deal/store";
import {
  exportStageDeals,
  fetchKanbanStageDeals,
  setKanbanHiddenColumnIds,
} from "@app/scene/deal/store/actions";
import { StagesWithDeals } from "@app/scene/deal/store/types/KanbanView";
import StageColumn from "@app/scene/deal/type/StageColumn";
import { dealKanbanViewAnalytics } from "@app/scene/deal/util/dealTableAnalytics";
import { hasPermission, isCurrentUserMember, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import ColumnFooter from "./components/ColumnFooter";
import ColumnSummaryInfo from "./components/ColumnSummaryInfo";
import DealCard from "./components/DealCard";
import styles from "./KanbanBoard.module.scss";
import { getColumnStyles, getItemListStyles } from "./util/styling";
import useBoardColumns from "./util/useBoardColumns";

interface Props {
  canDownload?: boolean;
  className?: string;
  exportStageDeals: typeof exportStageDeals;
  fetchKanbanStageDeals: typeof fetchKanbanStageDeals.request;
  groupedDeals: StagesWithDeals;
  hiddenColumnIds: BoardColumnId[];
  isMember: boolean;
  isReadOnlyMember: boolean;
  loading?: boolean;
  onChangeHiddenColumnIds: typeof setKanbanHiddenColumnIds;
  onDrop?: (destinationStage: Stage, deal: Deal) => void;
  onOpenFunnelSettings?: (funnelId: Funnel["id"], stageId?: Stage["id"]) => void;
  stages: Stage[];
}

const KanbanBoard: React.FC<Props> = ({
  canDownload,
  className,
  exportStageDeals,
  fetchKanbanStageDeals,
  groupedDeals,
  hiddenColumnIds,
  isMember,
  isReadOnlyMember,
  loading,
  onChangeHiddenColumnIds,
  onDrop,
  onOpenFunnelSettings,
  stages,
}) => {
  const intl = useIntl();

  const { register, unregister, updateViewStateAndFetch } = useView<Deal>();
  const [selectedStageForDownload, setSelectedStageForDownload] = useState<Stage | undefined>();
  useEffect(() => {
    const listener: ViewComponent<Deal> = {
      onViewStateChange: updateViewStateAndFetch,
    };
    register(listener);
    return () => {
      unregister(listener);
    };
  }, [register, unregister, updateViewStateAndFetch]);

  const handleDownload = useCallback(
    (columns: ColumnModel) => {
      if (selectedStageForDownload) {
        notification.info({
          message: intl.formatMessage({
            id: "deal.kanban.exportColumn",
            defaultMessage: "Export in progress...",
            description: "Deal kanban view - run export column",
          }),
        });
        exportStageDeals({ columns, stageId: selectedStageForDownload.id });
      }
    },
    [intl, exportStageDeals, selectedStageForDownload]
  );

  const columns = useBoardColumns(
    groupedDeals,
    stages,
    isMember || isReadOnlyMember,
    setSelectedStageForDownload,
    onOpenFunnelSettings,
    { canDownload }
  );

  const handleDrag = useCallback(
    (payload: { destination: BoardColumn<StageColumn, Deal>; item: Deal }) => {
      onDrop?.(payload.destination.data.stage, payload.item);
    },
    [onDrop]
  );

  const handleLoadMore = useCallback(
    (data: StageColumn) => {
      fetchKanbanStageDeals({ stageId: data.stage.id });
    },
    [fetchKanbanStageDeals]
  );

  return (
    <>
      <DnDBoard<StageColumn, Deal>
        analyticIssuer={dealKanbanViewAnalytics}
        className={cn(styles.container, className)}
        columnFooter={isReadOnlyMember ? undefined : ColumnFooter}
        columns={columns}
        columnSubHeader={ColumnSummaryInfo}
        getColumnStyles={getColumnStyles}
        getItemListStyles={getItemListStyles}
        hiddenColumnIds={hiddenColumnIds}
        itemComponent={DealCard}
        loading={loading}
        onChangeHiddenColumns={onChangeHiddenColumnIds}
        onDragged={isReadOnlyMember ? undefined : handleDrag}
        onLoadMore={handleLoadMore}
      />
      {!!selectedStageForDownload && (
        <DownloadEntitiesModal
          configurationKey="kanban"
          entityType={EntityType.DEAL}
          hasAdditionalFilters
          informationPoint={intl.formatMessage(
            {
              id: "deal.kanban.exportColumn.point1",
              defaultMessage: "All deals in <b>{name}</b> and their fields",
              description: "Deal kanban view - export information point 1",
            },
            { b: (text) => <b>{text}</b>, name: selectedStageForDownload.name }
          )}
          onDownload={handleDownload}
          onHide={() => setSelectedStageForDownload(undefined)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  hiddenColumnIds: getKanbanHiddenColumnIds(state),
  isMember: isCurrentUserMember(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  exportStageDeals,
  fetchKanbanStageDeals: fetchKanbanStageDeals.request,
  onChangeHiddenColumnIds: setKanbanHiddenColumnIds,
};

export default connect(mapStateToProps, mapDispatchToProps)(KanbanBoard);
